import { Layout, Typography, Row, Col, Card, Button, Carousel, Collapse, Divider, Spin } from 'antd';
import { CheckCircleOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import './style.css'; 
import { useNavigate, useParams } from 'react-router-dom';
import { useSystemDetail } from '~/hooks/system/useSystemDetail';
import { SystemHelmet } from '~/components/Helmet';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const extractTextFromDesc = (descArray) => {
  if (!Array.isArray(descArray)) return [];
  return descArray.flatMap((item) =>
    item?.children?.map((child) =>
      child?.children?.map((subChild) => subChild?.text)
    )
  ).flat().filter(Boolean);
};


const CustomPrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
        <div className={`custom-arrow left`} onClick={onClick} style={{ ...style }}>
            <LeftOutlined />
        </div>
    );
};

const CustomNextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
        <div className={`custom-arrow right`} onClick={onClick} style={{ ...style }}>
            <RightOutlined />
        </div>
    );
};

const BannerSlider = ({ banners }) => (
    <div className="banner-wrapper-system">
      <Carousel
        autoplay
        effect="fade"
        autoplaySpeed={2000}
        pauseOnHover
        pauseOnDotsHover
        dots={true}
      >
        {banners.map((banner) => (
          <div key={banner.id}>
            <div
              className="banner-slide"
              style={{
                backgroundImage: `url(${banner?.image?.url})`,
              }}
            >
              <div className="banner-overlay">
                <div className="banner-content">
                  <Title level={2} className="banner-title">
                    {banner?.title}
                  </Title>
                  <Paragraph className="banner-desc">
                    {banner?.shortDesc}
                  </Paragraph>
                </div>
              </div>
            </div>
          </div>
        ))}
      </Carousel>
    </div>
);

const BenefitsSection = ({ benefits }) => (
    <div className="benefits-section">
        <Title level={2} className="system-section-title">
            Lợi ích vượt trội cho doanh nghiệp
        </Title>
        <Row gutter={[40, 40]} justify="center">
        {benefits.map((benefit, index) => {
            const points = extractTextFromDesc(benefit.desc);

            return (
            <Col xs={24} sm={24} md={12} key={benefit.id}>
                <Card
                className="custom-benefit-card"
                style={{ animationDelay: `${index * 0.1}s` }}
                >
                <div className="card-content-wrapper">
                    <div className="benefit-icon-wrapper">
                      <img src={benefit?.icon?.url} alt="Tính năng hệ thống" className="feature-image" />
                    </div>

                    <div className="benefit-text-content">
                    <Title level={4} className="benefit-title">
                        {benefit.title}
                    </Title>

                    {points.map((text, idx) => (
                        <Paragraph key={idx} className="benefit-point">
                            <CheckCircleOutlined className="point-icon" /> {text}
                        </Paragraph>
                    ))}
                    </div>
                </div>
                </Card>
            </Col>
            );
        })}
        </Row>
    </div>
);

const FeaturesSection = ({ features }) => {
    if (!features) return null;
    const imageUrl = `${features[0]?.image?.url}`;
    return (
      <div className="section features-section" id="features">
          <Title level={2} className="system-section-title">Tính năng nổi bật</Title>
          <div className="feature-image-wrapper">
            <img src={imageUrl} alt="Tính năng hệ thống" className="feature-image" />
          </div>
      </div>
    )
}

const QuoteSection = ({ quote }) => {
  if (!quote) return null;
  const imageUrl = `${quote?.image?.url}`;
  return (
    <div
      className="quote-section"
      style={{ backgroundImage: `url(${imageUrl})` }}
    >
      <div className="quote-overlay" />
      <Card  className="quote-card">
        <Title level={4} className="quote-title">
          “{quote?.title}”
        </Title>

        <Paragraph className="quote-author">
          — {quote?.author}
        </Paragraph>
      </Card>
    </div>
  );
};

const PricingTable = ({ prices, title }) => {
    const navigate = useNavigate();

    const handleNavigate = (plan) => {
        navigate('/register-package', {
            state: {
                packageName: plan.title,
                solutionName: title,
            },
        });
    };

    return (
        <div className="section pricing-section" id="pricing">
            <Title level={2} className="system-section-title">Bảng Giá Dịch Vụ Linh Hoạt</Title>
            <Row gutter={[32, 32]} justify="center">
                {prices.map((plan, index) => (
                    <Col xs={24} sm={12} lg={8} key={plan.id}>
                        <Card
                            title={<Title level={4}>{plan.title}</Title>}
                            className={`pricing-card ${index === 1 ? 'standard' : ''}`}
                        >
                            <div className="price">{plan.subTitle}</div>
                            <div style={{ textAlign: 'left', minHeight: '150px' }}>
                                {plan.desc[0]?.children.map((item, i) => (
                                    <Paragraph key={i} style={{ fontSize: 16}}>
                                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 10 }} />
                                        {item.children[0].text}
                                    </Paragraph>
                                ))}
                            </div>
                            <Button
                                type='primary'
                                size="large"
                                block
                                style={{ marginTop: '20px' }}
                                onClick={() => handleNavigate(plan)}
                            >
                                Chọn Gói Này
                            </Button>
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

const CaseStudiesSlider = ({ studies }) => {
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        fade: true,
        prevArrow: <CustomPrevArrow />,
        nextArrow: <CustomNextArrow />,
        dotsClass: "slick-dots slick-thumb",
    };

    return (
        <div className="case-studies-section" id="case-studies">
            <Title level={2} className="system-section-title">Case Study</Title>
            <div className="case-studies-wrapper">
                <Carousel {...sliderSettings}>
                    {studies.map(study => (
                        <div key={study.id}>
                            <Card className="case-study-card" bordered={false}>
                                <Title level={4} className="case-study-title">{study.Title}</Title>
                                {study.contents.map((node, index) => {
                                    const children = node.children.map((child, childIndex) =>
                                        child.bold ? <strong key={childIndex}>{child.text}</strong> : child.text
                                    );
                                    if (node.type === 'paragraph') return <Paragraph className="case-paragraph" key={index}>{children}</Paragraph>;
                                    if (node.type === 'list') {
                                        return (
                                            <ul className="case-list" key={index}>
                                                {node.children.map((li, i) => <li key={i}><Text style={{ fontSize: 15}}>{li.children[0].text}</Text></li>)}
                                            </ul>
                                        );
                                    }
                                    return null;
                                })}
                            </Card>
                        </div>
                    ))}
                </Carousel>
            </div>
        </div>
    );
};

const SystemDetailPage = () => {
    const { documentId } = useParams();
    const { data, isLoading, isError } = useSystemDetail(documentId);
    const bannerImage = data?.banners?.[0]?.image?.url;
    const shortDesc = data?.banners?.[0]?.shortDesc;
    if (isLoading)
    return (
      <div className="custom-loading">
        <div className="loader"></div>
        <p>Đang tải dữ liệu...</p>
      </div>
    );
    if (isError || !data) return <div>Không tìm thấy dữ liệu</div>;
    if (!data) return null;
    
    const { banners, benifits, features, quote, prices, caseStudies, title } = data;
    return (
            <Content>
                <SystemHelmet keySystem={data?.key} slug={documentId} defaultDesc={shortDesc} bannerImage={bannerImage} />
                {/* 1. Banner Slider */}
                {banners && banners.length > 0 && <BannerSlider banners={banners} />}

                {/* 2. Lợi ích & Tính năng */}
                {benifits && benifits.length > 0 && (
                    <BenefitsSection benefits={benifits} />
                )}

                {/* 3. Tính năng của hệ thống */}
                {features && features.length > 0 && <FeaturesSection features={features} />}

                {/* 3. Quote */}
                {quote && quote.length > 0 && <QuoteSection quote={quote[0]} />}

                {/* 4. Bảng giá — ẩn theo yêu cầu */}
                {/* {prices && prices.length > 0 && <PricingTable prices={prices} title={title}/>} */}
                
                {/* 5. Case Studies */}
                {caseStudies && caseStudies.length > 0 && <CaseStudiesSlider studies={caseStudies} />}
            </Content>
    );
};

export default SystemDetailPage;